import type { ReactNode } from 'react';
import type { Key } from 'react-aria';
import type { ListBoxRenderProps } from 'react-aria-components';
import { msg } from '@gonfalon/messages/msg';
import { RequiredLabel } from '@gonfalon/ui/RequiredLabel';
import type { ComboBoxProps } from '@launchpad-ui/components';
import {
  Group,
  IconButton,
  Input,
  ComboBox as LaunchpadComboBox,
  ListBox,
  ListBoxItem,
  Popover,
} from '@launchpad-ui/components';

import type { ComboBoxItem } from '../../types';

import { FormFieldError } from './FormFieldError';
import {
  type BrowserExtensionControlProps,
  getDisableBrowserExtensionAttributes,
} from './getDisableBrowserExtensionAttributes';
import { useField } from './useField';

export type Props<T extends object> = Omit<ComboBoxProps<T>, 'children' | 'selectedKey' | 'isInvalid'> &
  BrowserExtensionControlProps & {
    renderEmptyState?: (props: ListBoxRenderProps) => ReactNode;
    label?: ReactNode;
    items?: Iterable<ComboBoxItem>;
  };

export const ComboBox = <T extends object>({
  disableBrowserExtensions = true,
  label,
  items,
  isRequired,
  'aria-label': ariaLabel,
  onBlur: onBlurChange,
  onSelectionChange,
  renderEmptyState,
  ...props
}: Props<T>) => {
  const { field, onChange, onBlur, value, isInvalid } = useField<string>({
    onChange: onSelectionChange,
    onBlur: onBlurChange,
  });

  return (
    <LaunchpadComboBox
      isInvalid={isInvalid}
      onSelectionChange={(key: Key | null) => onChange(key === null ? '' : String(key))}
      onBlur={onBlur}
      selectedKey={value}
      name={field.name}
      aria-label={ariaLabel}
      {...props}
    >
      {label && <RequiredLabel isRequired={isRequired}>{label}</RequiredLabel>}
      <Group>
        <Input
          aria-label={ariaLabel}
          data-test-id={`${field.name || ariaLabel}-input`}
          autoComplete="chrome-off"
          {...getDisableBrowserExtensionAttributes(disableBrowserExtensions)}
        />
        <IconButton
          aria-label={msg('ui.comboBox.showOptionsLabel')}
          icon="chevron-down"
          size="small"
          variant="minimal"
        />
      </Group>

      <FormFieldError meta={field.state.meta} />
      <Popover>
        <ListBox items={items} renderEmptyState={renderEmptyState}>
          {(item) => (
            <ListBoxItem
              key={item.value}
              id={item.value}
              textValue={item.label}
              aria-label={item.label}
              data-test-id={`${field.name || ariaLabel}-option`}
              ref={item.ref}
              isDisabled={item.isDisabled}
            >
              {item.icon}
              {item.label}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </LaunchpadComboBox>
  );
};
